import React, { useState, useEffect } from 'react';
import {
  Download,
  Smartphone,
  Share,
  PlusSquare,
  CheckCircle2,
  X,
  WifiOff,
  MoreVertical,
  Monitor,
  ShieldCheck,
} from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';

interface InstallAppButtonProps {
  variant?: 'navbar' | 'mobile-drawer' | 'footer' | 'hero';
  onAfterClick?: () => void;
}

export const InstallAppButton: React.FC<InstallAppButtonProps> = ({
  variant = 'navbar',
  onAfterClick,
}) => {
  const { isStandalone, isIOS, hasNativeInstallPrompt, triggerInstall } = usePWAInstall();

  // Hide install button when already running inside the installed standalone PWA
  if (isStandalone) {
    return null;
  }

  const handleClick = async () => {
    await triggerInstall();
    if (onAfterClick) {
      onAfterClick();
    }
  };

  if (variant === 'navbar') {
    return (
      <button
        type="button"
        id="navbar-install-app-btn"
        onClick={handleClick}
        title={
          isIOS
            ? 'Install JobMatch NG on your iPhone Home Screen'
            : 'Install JobMatch NG App'
        }
        className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 hover:bg-emerald-50 dark:bg-slate-800 dark:hover:bg-emerald-950/60 text-slate-800 hover:text-emerald-700 dark:text-slate-200 dark:hover:text-emerald-300 border border-slate-200/80 dark:border-slate-700 text-xs font-bold transition-all min-h-[40px] cursor-pointer shrink-0"
      >
        <Download className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
        <span>Install App</span>
      </button>
    );
  }

  if (variant === 'mobile-drawer') {
    return (
      <button
        type="button"
        id="mobile-drawer-install-app-btn"
        onClick={handleClick}
        className="w-full py-3 px-3.5 rounded-2xl text-xs font-extrabold text-emerald-900 dark:text-emerald-200 bg-emerald-50/90 dark:bg-emerald-950/60 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 border border-emerald-200 dark:border-emerald-800/80 min-h-[46px] flex items-center justify-between gap-2 shadow-2xs transition cursor-pointer"
      >
        <span className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          <span>
            {isIOS
              ? 'Install on iPhone (Add to Home Screen)'
              : hasNativeInstallPrompt
              ? 'Install JobMatch NG App'
              : 'Install App on Your Device'}
          </span>
        </span>
        <span className="px-2 py-0.5 rounded-md bg-emerald-600 text-white text-[10px] font-bold">
          {isIOS ? 'iOS Guide' : 'Install'}
        </span>
      </button>
    );
  }

  if (variant === 'footer') {
    return (
      <button
        type="button"
        id="footer-install-app-btn"
        onClick={handleClick}
        className="w-full py-2.5 px-3 rounded-xl bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/30 text-emerald-300 font-bold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer"
      >
        <Download className="w-4 h-4 text-emerald-400 shrink-0" />
        <span>{isIOS ? 'Install on iPhone Home Screen' : 'Install JobMatch NG App'}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-sm transition cursor-pointer"
    >
      <Download className="w-4 h-4 shrink-0" />
      <span>Install App</span>
    </button>
  );
};

export const PWAInstallManager: React.FC = () => {
  const {
    isStandalone,
    isIOS,
    isAndroid,
    isOnline,
    hasNativeInstallPrompt,
    isBannerDismissed,
    isGuideModalOpen,
    triggerInstall,
    openGuideModal,
    closeGuideModal,
    dismissBanner,
  } = usePWAInstall();

  const [activeTab, setActiveTab] = useState<'ios' | 'android' | 'desktop'>(() => {
    if (isIOS) return 'ios';
    if (isAndroid) return 'android';
    return 'android';
  });

  useEffect(() => {
    if (isIOS) {
      setActiveTab('ios');
    } else if (isAndroid) {
      setActiveTab('android');
    }
  }, [isIOS, isAndroid]);

  const showFloatingBanner =
    !isStandalone &&
    !isBannerDismissed &&
    (hasNativeInstallPrompt || isIOS || isAndroid);

  return (
    <>
      {/* 1. Safe Offline Status Indicator Banner */}
      {!isOnline && (
        <div
          id="pwa-offline-banner"
          role="status"
          aria-live="polite"
          className="bg-amber-950 text-amber-100 border-b border-amber-800/80 px-4 py-2 text-xs font-semibold flex items-center justify-center gap-2 text-center z-50"
        >
          <WifiOff className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <span>
            You’re offline. Viewing cached public pages — live quotes, sign-in, and Paystack escrow require internet.
          </span>
        </div>
      )}

      {/* 2. Attractive Bottom Install Banner (Android Native Prompt & iPhone Home Screen Guide) */}
      {showFloatingBanner && (
        <div
          id="pwa-install-floating-banner"
          role="region"
          aria-label="Install JobMatch NG App"
          className="fixed bottom-20 md:bottom-5 left-4 right-4 md:left-6 md:right-auto md:w-[400px] z-40 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-2xl transition-all"
        >
          <div className="flex items-start gap-3">
            <img
              src="/pwa-192x192.png"
              alt="JobMatch NG App Icon"
              className="w-11 h-11 rounded-xl object-contain bg-white border border-slate-200 dark:border-slate-700 p-1 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white tracking-tight">
                    Install JobMatch NG App
                  </h3>
                  <p className="text-[11px] text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">
                    {isIOS
                      ? 'Add JobMatch NG to your iPhone Home Screen for fast 1-tap access and instant push alerts.'
                      : 'Install our lightweight app for faster booking, instant job alerts, and offline access.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={dismissBanner}
                  aria-label="Dismiss install banner"
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  type="button"
                  onClick={dismissBanner}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
                >
                  Later
                </button>
                {isIOS ? (
                  <button
                    type="button"
                    id="pwa-banner-ios-guide-btn"
                    onClick={openGuideModal}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-extrabold bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs transition cursor-pointer"
                  >
                    <Share className="w-3.5 h-3.5" />
                    <span>How to Install on iPhone</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    id="pwa-banner-install-btn"
                    onClick={() => void triggerInstall()}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-extrabold bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs transition cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Install App</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. Interactive Install App & iOS Add to Home Screen Modal */}
      {isGuideModalOpen && (
        <div
          id="pwa-install-guide-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pwa-install-modal-title"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-5 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <img
                  src="/pwa-192x192.png"
                  alt="JobMatch NG"
                  className="w-12 h-12 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white p-1"
                />
                <div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-emerald-50 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                    OFFICIAL WEB APP
                  </span>
                  <h2
                    id="pwa-install-modal-title"
                    className="text-base font-extrabold text-slate-900 dark:text-white mt-0.5"
                  >
                    Install JobMatch NG
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={closeGuideModal}
                aria-label="Close installation guide"
                className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Platform Selector Tabs */}
            <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 dark:bg-slate-800/90 rounded-2xl text-xs font-bold">
              <button
                type="button"
                onClick={() => setActiveTab('ios')}
                className={`py-2 px-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 ${
                  activeTab === 'ios'
                    ? 'bg-white dark:bg-slate-900 text-emerald-700 dark:text-emerald-400 shadow-2xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span>iPhone / iOS</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('android')}
                className={`py-2 px-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 ${
                  activeTab === 'android'
                    ? 'bg-white dark:bg-slate-900 text-emerald-700 dark:text-emerald-400 shadow-2xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Download className="w-3.5 h-3.5" />
                <span>Android</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('desktop')}
                className={`py-2 px-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 ${
                  activeTab === 'desktop'
                    ? 'bg-white dark:bg-slate-900 text-emerald-700 dark:text-emerald-400 shadow-2xs'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Monitor className="w-3.5 h-3.5" />
                <span>Desktop</span>
              </button>
            </div>

            {/* Tab Content: iPhone / iPad Safari */}
            {activeTab === 'ios' && (
              <div className="space-y-3.5">
                <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                  Install JobMatch NG on your iPhone or iPad Home Screen in 3 quick steps using <strong>Safari</strong>:
                </p>

                <ol className="space-y-2.5 text-xs">
                  <li className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white font-extrabold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div className="flex-1">
                      <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                        <span>Tap the Safari Share button</span>
                        <Share className="w-4 h-4 text-emerald-600 dark:text-emerald-400 inline" />
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Located at the bottom bar of Safari on iPhone (or top bar on iPad).
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white font-extrabold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div className="flex-1">
                      <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                        <span>Select “Add to Home Screen”</span>
                        <PlusSquare className="w-4 h-4 text-emerald-600 dark:text-emerald-400 inline" />
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Scroll down the Share menu and tap <strong>Add to Home Screen</strong>.
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800">
                    <span className="w-6 h-6 rounded-full bg-emerald-600 text-white font-extrabold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div className="flex-1">
                      <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                        <span>Tap “Add” &amp; launch from Home Screen</span>
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 inline" />
                      </p>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Open JobMatch NG from your Home Screen icon to enable instant iOS Push Notifications (iOS 16.4+).
                      </p>
                    </div>
                  </li>
                </ol>
              </div>
            )}

            {/* Tab Content: Android */}
            {activeTab === 'android' && (
              <div className="space-y-3.5">
                {hasNativeInstallPrompt ? (
                  <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/70 space-y-3">
                    <p className="text-xs font-bold text-emerald-950 dark:text-emerald-200">
                      Ready for 1-Tap Android Installation
                    </p>
                    <p className="text-[11px] text-emerald-800 dark:text-emerald-300 leading-relaxed">
                      Tap the button below to add JobMatch NG directly to your Android Home Screen and app drawer.
                    </p>
                    <button
                      type="button"
                      onClick={() => void triggerInstall()}
                      className="w-full py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs flex items-center justify-center gap-2 shadow-sm transition cursor-pointer"
                    >
                      <Download className="w-4 h-4" />
                      <span>Install JobMatch NG Now</span>
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                      Install JobMatch NG on your Android phone using <strong>Chrome</strong>, <strong>Edge</strong>, or <strong>Samsung Internet</strong>:
                    </p>
                    <ol className="space-y-2.5 text-xs">
                      <li className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800">
                        <span className="w-6 h-6 rounded-full bg-emerald-600 text-white font-extrabold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
                          1
                        </span>
                        <div className="flex-1">
                          <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
                            <span>Tap the Browser Menu</span>
                            <MoreVertical className="w-4 h-4 text-emerald-600 dark:text-emerald-400 inline" />
                          </p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                            Tap the three-dot menu icon in the top-right corner of Chrome.
                          </p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800">
                        <span className="w-6 h-6 rounded-full bg-emerald-600 text-white font-extrabold text-[11px] flex items-center justify-center shrink-0 mt-0.5">
                          2
                        </span>
                        <div className="flex-1">
                          <p className="font-bold text-slate-900 dark:text-white">
                            Tap “Install app” or “Add to Home screen”
                          </p>
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                            Confirm by tapping <strong>Install</strong> to place JobMatch NG on your Home Screen.
                          </p>
                        </div>
                      </li>
                    </ol>
                  </>
                )}
              </div>
            )}

            {/* Tab Content: Desktop */}
            {activeTab === 'desktop' && (
              <div className="space-y-3.5">
                {hasNativeInstallPrompt ? (
                  <div className="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/70 space-y-3">
                    <p className="text-xs font-bold text-emerald-950 dark:text-emerald-200">
                      Install Desktop App
                    </p>
                    <p className="text-[11px] text-emerald-800 dark:text-emerald-300 leading-relaxed">
                      Run JobMatch NG in a dedicated standalone window with instant desktop notifications.
                    </p>
                    <button
                      type="button"
                      onClick={() => void triggerInstall()}
                      className="w-full py-2.5 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs flex items-center justify-center gap-2 shadow-sm transition cursor-pointer"
                    >
                      <Download className="w-4 h-4" />
                      <span>Install Desktop App</span>
                    </button>
                  </div>
                ) : (
                  <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/70 dark:border-slate-800 space-y-2 text-xs">
                    <p className="font-bold text-slate-900 dark:text-white">
                      Install via Chrome or Microsoft Edge
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed">
                      Click the <strong>Install JobMatch NG</strong> icon on the right side of your browser’s address bar, or open the browser menu (<strong>⋮</strong>) &rarr; <strong>Save and share</strong> &rarr; <strong>Install JobMatch NG</strong>.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Security & Storage Reassurance */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                <ShieldCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                <span>Fast, lightweight &amp; verified secure</span>
              </div>
              <button
                type="button"
                onClick={closeGuideModal}
                className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white text-xs font-bold transition cursor-pointer"
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
